import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { AppView, FormValue, UserStore, ViewStore } from '@coach-bot/data-access/shared';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '@shared/ui';
import { BehaviorSubject, Observable, combineLatest, debounceTime, map, takeUntil } from 'rxjs';
import { FORM_VALUE_CHANGES_DEBOUNCE_TIME_MS } from '../../constants';
import { AuthState } from '../../states';
import { AbstractAuthComponent } from '../abstract/abstract-auth.component';

@Component({
  selector: 'fu-forgot-password',
  templateUrl: './forgot-password.component.html',
  styleUrls: ['../../styles/auth.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ForgotPasswordComponent extends AbstractAuthComponent implements OnInit, AfterViewInit {
  readonly actionDisabled$: Observable<boolean>;
  readonly isFormValid$ = new BehaviorSubject<boolean>(false);

  constructor(
    protected override authState: AuthState,
    protected override notificationService: NotificationService,
    private changeDetectorRef: ChangeDetectorRef,
    protected override viewStore: ViewStore,
    private formBuilder: FormBuilder,
    private translateService: TranslateService,
    private userStore: UserStore
  ) {
    super(authState, notificationService, viewStore);

    this.actionDisabled$ = combineLatest([this.isFormValid$, this.loading$]).pipe(
      takeUntil(this.destroyed$),
      map(([isFormValid, loading]: [boolean, boolean]) => !isFormValid || loading)
    );
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
    });

    if (this.userStore.forgotPasswordFormValue$.value) {
      this.form.setValue(this.userStore.forgotPasswordFormValue$.value);
    }

    this.updateIsFormValid();

    this.form.valueChanges
      .pipe(debounceTime(FORM_VALUE_CHANGES_DEBOUNCE_TIME_MS), takeUntil(this.destroyed$))
      .subscribe((value: FormValue) => {
        this.updateIsFormValid();
        this.userStore.setForgotPasswordFormValue(value);
      });

    this.actionDisabled$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.changeDetectorRef.detectChanges();
    });
  }

  override ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.authState.sendResetLinkSuccessfull$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.notificationService.showNotification(
        this.translateService.instant('auth.forgot_password.labels.success'),
        'success',
        this.container,
      );
    });
  }

  signIn(): void {
    this.viewStore.setView(AppView.SignIn);
  }

  sendResetLink(): void {
    if (this.form.valid) {
      this.authState.forgotPassword(this.form.value.email);
    }
  }

  private updateIsFormValid(): void {
    if (this.isFormValid$.value !== this.form.valid) {
      this.isFormValid$.next(this.form.valid);
    }
  }
}
